import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarLightIcon,
  ChevronLeftIcon,
  OutlineLocationIcon,
  SelectIcon,
} from "@App/Components/common/Icons";
import { Button } from "@App/Components/Funnel/Button";
import { Input } from "@App/Components/common/Input";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { addDays, isAfter, isSameDay, parseISO } from "date-fns";
import { isHoliday, Region } from "feiertagejs";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { actionCreators, State } from "@App/state";
import { useRegion } from "@App/hooks/useRegion";
import { addBusinessDays, formatDate, isWeekend } from "@App/utils/dates";
import { Headline } from "@App/Components/atoms/common/Headline";

export type DeliveryDetailsProps = {
  onSubmit: () => void;
};
export const DeliveryDetails: FunctionComponent<DeliveryDetailsProps> = ({
  onSubmit,
}: DeliveryDetailsProps) => {
  const { t } = useTranslation(); // i18next

  const navigate = useNavigate();

  const contractDetails = useSelector((state: State) => state.orders.contractDetails);

  const dispatch = useDispatch();
  const { updateContractDetails, setDeliveryDates } = bindActionCreators(actionCreators, dispatch);

  const [addressModal, setAddressModal] = useState(false);

  const date1 = useSelector((state: State) => state.orders.deliveryDates.date1);
  const date2 = useSelector((state: State) => state.orders.deliveryDates.date2);
  const date3 = useSelector((state: State) => state.orders.deliveryDates.date3);

  const [selectedDate1, setSelectedDate1] = useState(date1);
  const [selectedDate2, setSelectedDate2] = useState(date2);
  const [selectedDate3, setSelectedDate3] = useState(date3);

  const { name, street, postcode, city, phone } = contractDetails;

  const [newName, setNewName] = useState(name);
  const [newStreet, setNewStreet] = useState(street);
  const [newPostcode, setNewPostcode] = useState(postcode);
  const [newCity, setNewCity] = useState(city);
  const [maxDays, setMaxDays] = useState(9);
  const [newPhone, setNewPhone] = useState(phone);

  const [region, setRegion] = useState<Region>("BUND");

  const { getRegionAbbreviation } = useRegion();

  useEffect(() => {
    setRegion(getRegionAbbreviation(postcode));
  }, [postcode]);

  const isSelectedDay = (date) => {
    return (
      (selectedDate1 && isSameDay(date, selectedDate1)) ||
      (selectedDate2 && isSameDay(date, selectedDate2)) ||
      (selectedDate3 && isSameDay(date, selectedDate3))
    );
  };

  const checkDay = (date) => {
    return (
      isSelectedDay(date) ||
      isWeekend(date) ||
      isHoliday(date, region) ||
      isAfter(parseISO(contractDetails.earliestDeliveryDate), date)
    );
  };

  // check if earliest delivery date is in the future
  function calculateEarliestDeliveryDate(contractDetails) {
    const { earliestDeliveryDate } = contractDetails;
    const parsedDate = parseISO(earliestDeliveryDate);

    if (isAfter(parsedDate, addBusinessDays(new Date(), 4, region))) {
      return parsedDate;
    }

    return addBusinessDays(new Date(), 4, region);
  }

  const earliestDeliveryDate = calculateEarliestDeliveryDate(contractDetails);

  const minDate = earliestDeliveryDate;
  const maxDate = addDays(earliestDeliveryDate, maxDays);

  const changeAddress = () => {
    updateContractDetails(contractDetails, {
      name: newName,
      street: newStreet,
      postcode: newPostcode,
      city: newCity,
      phone: newPhone,
    });
    setAddressModal(false);
  };

  const submit = () => {
    updateContractDetails(contractDetails, {
      street,
      postcode,
      city,
      phone,
    });
    setDeliveryDates({
      date1: selectedDate1,
      date2: selectedDate2,
      date3: selectedDate3,
    });
    onSubmit();
  };

  // redirect back to start if parameters are missing
  useEffect(() => {
    if (!name || !street || !postcode || !city) {
      navigate("/partner/mobility-concept/fahrzeuglieferung");
    }
  }, []);

  return (
    <Fragment>
      <div className="container md:w-[738px] md:min-h-[calc(100vh-200px)] flex items-start my-10 mx-auto">
        <div>
          <Headline type="h1">{t("vehicleDelivery.details-headline")}</Headline>
          <div className="w-full md:w-1/2 flex shadow rounded-lg p-5 mb-10">
            <OutlineLocationIcon className={"w-5 h-w mr-3"} color={"primary-900"} />
            <div>
              <div className={"font-bold"}>{t("vehicleDelivery.details-address-headline")}</div>
              <div>{name}</div>
              <div>{phone}</div>
              <div>{street}</div>
              <div>
                {postcode} {city}
              </div>
              <div
                className={"text-primary cursor-pointer mt-3"}
                onClick={() => setAddressModal(true)}
              >
                Ändern
              </div>
            </div>
          </div>
          <h2 className={"text-xl font-serif mb-3"}>
            {t("vehicleDelivery.details-date-headline")}
          </h2>
          <p className={"text-neutral-500 mb-5"}>{t("vehicleDelivery.details-date-text")}</p>

          <div className={"flex flex-col md:flex-row justify-between gap-3 mt-3"}>
            <Popover className={"relative flex flex-col cursor-pointer"}>
              <label htmlFor="date-1">{t("vehicleDelivery.details-date-1")}</label>
              <PopoverButton className={"flex relative"}>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {<CalendarLightIcon className={"h-5 w-5"} color={"primary-900"} />}
                  </div>
                  <input
                    className={"border border-neutral-300 rounded shadow pl-9 py-2 px-3 my-0"}
                    id="date-1"
                    type="text"
                    placeholder={"TT/MM/JJJJ"}
                    value={(selectedDate1 && formatDate(selectedDate1, "dd.MM.yyyy")) || ""}
                    onChange={() => console.log("")}
                  />
                  <div className="absolute right-4 top-3">
                    <SelectIcon color={"neutral-500"} />
                  </div>
                </div>
              </PopoverButton>
              <PopoverPanel>
                <Calendar
                  minDate={minDate}
                  maxDate={maxDate}
                  defaultActiveStartDate={earliestDeliveryDate}
                  onChange={setSelectedDate1}
                  value={selectedDate1}
                  className={`
                                        !absolute !border-0 top-16 left-0 shadow rounded-lg
                                        font-serif p-3 z-10
                                    `}
                  tileDisabled={({ date }) => checkDay(date)}
                  minDetail="month"
                  locale={"de-DE"}
                  prevLabel={<ChevronLeftIcon className={"w-3 h-w"} color={"primary"} />}
                  nextLabel={<ChevronLeftIcon className={"w-3 h-w rotate-180"} color={"primary"} />}
                />
              </PopoverPanel>
            </Popover>

            <Popover className={"relative flex flex-col cursor-pointer"}>
              <label htmlFor="date-2">{t("vehicleDelivery.details-date-2")}</label>
              <PopoverButton className={"flex relative"}>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {<CalendarLightIcon className={"h-5 w-5"} color={"primary-900"} />}
                  </div>
                  <input
                    className={"border border-neutral-300 rounded shadow pl-9 py-2 px-3 my-0"}
                    id="date-2"
                    type="text"
                    placeholder={"TT/MM/JJJJ"}
                    value={(selectedDate2 && formatDate(selectedDate2, "dd.MM.yyyy")) || ""}
                    onChange={() => {}}
                  />
                  <div className="absolute right-4 top-3">
                    <SelectIcon color={"neutral-500"} />
                  </div>
                </div>
              </PopoverButton>
              <PopoverPanel>
                <Calendar
                  minDate={minDate}
                  maxDate={maxDate}
                  defaultActiveStartDate={earliestDeliveryDate}
                  onChange={setSelectedDate2}
                  value={selectedDate2}
                  className={`
                                        !absolute !border-0 top-16 left-0 shadow rounded-lg
                                        font-serif p-3 z-10
                                    `}
                  tileDisabled={({ date }) => checkDay(date)}
                  minDetail="month"
                  locale={"de-DE"}
                  prevLabel={<ChevronLeftIcon className={"w-3 h-w"} color={"primary"} />}
                  nextLabel={<ChevronLeftIcon className={"w-3 h-w rotate-180"} color={"primary"} />}
                />
              </PopoverPanel>
            </Popover>

            <Popover className={"relative flex flex-col cursor-pointer"}>
              <label htmlFor="date-3">{t("vehicleDelivery.details-date-3")}</label>
              <PopoverButton className={"flex relative"}>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {<CalendarLightIcon className={"h-5 w-5"} color={"primary-900"} />}
                  </div>
                  <input
                    className={"border border-neutral-300 rounded shadow pl-9 py-2 px-3 my-0"}
                    id="date-3"
                    type="text"
                    placeholder={"TT/MM/JJJJ"}
                    value={(selectedDate3 && formatDate(selectedDate3, "dd.MM.yyyy")) || ""}
                    onChange={() => {}}
                  />
                  <div className="absolute right-4 top-3">
                    <SelectIcon color={"neutral-500"} />
                  </div>
                </div>
              </PopoverButton>
              <PopoverPanel>
                <Calendar
                  minDate={minDate}
                  maxDate={maxDate}
                  defaultActiveStartDate={earliestDeliveryDate}
                  onChange={setSelectedDate3}
                  value={selectedDate3}
                  className={`
                                        !absolute !border-0 top-16 left-0 shadow rounded-lg
                                        font-serif p-3 z-10
                                    `}
                  tileDisabled={({ date }) => checkDay(date)}
                  minDetail="month"
                  locale={"de-DE"}
                  prevLabel={<ChevronLeftIcon className={"w-3 h-w"} color={"primary"} />}
                  nextLabel={<ChevronLeftIcon className={"w-3 h-w rotate-180"} color={"primary"} />}
                />
              </PopoverPanel>
            </Popover>
          </div>
          {maxDays === 9 ? (
            <div className={"text-primary cursor-pointer my-3"} onClick={() => setMaxDays(18)}>
              {t("vehicleDelivery.details-hint")}
            </div>
          ) : (
            <div className={"text-success my-3"}>{t("vehicleDelivery.details-hint-accepted")}</div>
          )}
          <Button
            title={t("vehicleDelivery.details-button")}
            onClick={submit}
            disabled={!selectedDate1 || !selectedDate2 || !selectedDate3}
          />

          {addressModal && (
            <div className="flex justify-center items-center absolute top-0 left-0 w-full h-screen">
              <div
                className="fixed top-0 left-0 w-full h-screen bg-neutral-700 opacity-75 z-20"
                onClick={() => setAddressModal(false)}
              ></div>
              <div className="w-full sm:w-[335px] bg-white rounded-lg shadow-lg p-5 m-3 z-30">
                <div className="text-center mb-5">
                  <div className="inline-flex justify-center items-center rounded-full bg-neutral-100 py-4 px-5 mb-5">
                    <OutlineLocationIcon className={"w-3"} />
                  </div>
                  <div className="text-xl font-bold font-serif mb-3">
                    {t("vehicleDelivery.details-modal-headline")}
                  </div>
                  <p
                    className={"text-sm text-neutral-500"}
                    dangerouslySetInnerHTML={{
                      __html: t("vehicleDelivery.details-modal-text"),
                    }}
                  ></p>
                </div>
                <div>
                  <label htmlFor="name">Name</label>
                  <Input
                    id="name"
                    type="text"
                    className={"w-full mt-0 mb-3"}
                    value={newName}
                    onChange={setNewName}
                  />
                </div>
                <div>
                  <label htmlFor="phone">Telefon</label>
                  <Input
                    id="phone"
                    type="tel"
                    className={"w-full mt-0 mb-3"}
                    value={newPhone}
                    onChange={setNewPhone}
                  />
                </div>
                <div>
                  <label htmlFor="street">Straße & Hausnummer</label>
                  <Input
                    id="street"
                    type="text"
                    className={"w-full mt-0 mb-3"}
                    value={newStreet}
                    onChange={setNewStreet}
                  />
                </div>
                <div>
                  <label htmlFor="postcode">Postleitzahl</label>
                  <Input
                    id="postcode"
                    type="text"
                    className={"w-1/3 mt-0 mb-3"}
                    value={newPostcode}
                    onChange={setNewPostcode}
                  />
                </div>
                <div>
                  <label htmlFor="city">Ort</label>
                  <Input
                    id="city"
                    type="text"
                    className={"w-full mt-0 mb-3"}
                    value={newCity}
                    onChange={setNewCity}
                  />
                </div>

                <div className="text-center mb-5">
                  <p className={"text-sm text-neutral-500"}>
                    {t("vehicleDelivery.details-modal-hint")}
                  </p>
                </div>

                <div className="flex justify-between gap-3 mt-3">
                  <Button
                    title={t("vehicleDelivery.details-modal-cancel")}
                    className={"md:!w-1/2"}
                    outline
                    onClick={() => setAddressModal(false)}
                  />
                  <Button
                    title={t("vehicleDelivery.details-modal-accept")}
                    className={"md:!w-1/2"}
                    onClick={changeAddress}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};
